import * as Sentry from '@sentry/browser';

let visit;

const pageCache             = {};
let cacheSize               = 10;
let transitionCacheEnabled  = false;
let progressBar             = null;

let currentState            = null;
let loadedAssets            = null;

let referer                 = null;

let xhr                     = null;

const EVENTS = {
  BEFORE_CHANGE:  'page:before-change',
  FETCH:          'page:fetch',
  RECEIVE:        'page:receive',
  CHANGE:         'page:change',
  UPDATE:         'page:update',
  LOAD:           'page:load',
  RESTORE:        'page:restore',
  BEFORE_UNLOAD:  'page:before-unload',
  EXPIRE:         'page:expire'
};

const PERMANENT_ELEMENTS = {};

function fetch(url) {
  let cachedPage;
  url = new ComponentUrl(url);

  rememberReferer();
  cacheCurrentPage();
  if (progressBar != null) {
    progressBar.start();
  }

  if (transitionCacheEnabled && (cachedPage = transitionCacheFor(url.absolute))) {
    fetchHistory(cachedPage);
    return fetchReplacement(url, null, false);
  } else {
    return fetchReplacement(url, resetScrollPosition);
  }
};

function transitionCacheFor(url) {
  const cachedPage = pageCache[url];
  if (cachedPage && !cachedPage.transitionCacheDisabled) { return cachedPage; }
};

function enableTransitionCache(enable = true) {
  return transitionCacheEnabled = enable;
};

function enableProgressBar(enable = true) {
  if (!browserSupportsTurbolinks) { return; }
  if (enable) {
    return progressBar != null ? progressBar : (progressBar = new ProgressBar('html'));
  } else {
    if (progressBar != null) {
      progressBar.uninstall();
    }
    return progressBar = null;
  }
};

function fetchReplacement(url, onLoadFunction, showProgressBar = true) {
  triggerEvent(EVENTS.FETCH, {url: url.absolute});

  if (xhr != null) {
    xhr.abort();
  }
  const newXhr = new XMLHttpRequest;
  newXhr.open('GET', url.withoutHashForIE10compatibility(), true);
  newXhr.setRequestHeader('Accept', 'text/html, application/xhtml+xml, application/xml');
  newXhr.setRequestHeader('X-XHR-Referer', referer);

  newXhr.onload = function() {
    triggerEvent(EVENTS.RECEIVE, {url: url.absolute});

    const doc = processResponse(newXhr);
    if (doc) {
      reflectNewUrl(url);
      reflectRedirectedUrl(newXhr);
      changePage(...Array.from(extractTitleAndBody(doc) || []));
      manuallyTriggerHashChangeForFirefox();
      if (typeof onLoadFunction === 'function') {
        onLoadFunction();
      }
      return triggerEvent(EVENTS.LOAD);
    } else {
      return document.location.href = crossOriginRedirect(newXhr) || url.absolute;
    }
  };

  if (progressBar && showProgressBar) {
    newXhr.onprogress = event => {
      const percent = event.lengthComputable ?
        (event.loaded / event.total) * 100
      :
        progressBar.value + ((100 - progressBar.value) / 10);
      return progressBar.advanceTo(percent);
    };
  }

  newXhr.onloadend = () => xhr = null;
  newXhr.onerror   = () => document.location.href = url.absolute;

  xhr = newXhr;
  return newXhr.send();
};

function fetchHistory(cachedPage) {
  if (xhr != null) {
    xhr.abort();
  }
  changePage(cachedPage.title, cachedPage.body);
  recallScrollPosition(cachedPage);
  return triggerEvent(EVENTS.RESTORE);
};


function cacheCurrentPage() {
  if (!currentState) { return; }

  const currentStateUrl = new ComponentUrl(currentState.url);

  pageCache[currentStateUrl.absolute] = {
    url:                      currentStateUrl.relative,
    body:                     document.body,
    title:                    document.title,
    positionY:                window.pageYOffset,
    positionX:                window.pageXOffset,
    cachedAt:                 new Date().getTime(),
    transitionCacheDisabled:  (document.querySelector('[data-no-transition-cache]') != null)
  };

  return constrainPageCacheTo(cacheSize);
};

function pagesCached(size = cacheSize) {
  if (/^[\d]+$/.test(size)) { return cacheSize = parseInt(size); }
};

function constrainPageCacheTo(limit) {
  const pageCacheKeys = Object.keys(pageCache);

  const cacheTimesRecentFirst = pageCacheKeys.map(url => pageCache[url].cachedAt).sort((a, b) => b - a);

  for (let key of pageCacheKeys) {
    if (pageCache[key].cachedAt <= cacheTimesRecentFirst[limit]) {
      triggerEvent(EVENTS.EXPIRE, pageCache[key]);
      delete pageCache[key];
    }
  }
};

function changePage(title, body, csrfToken, canonicalUrl, runScripts) {
  triggerEvent(EVENTS.BEFORE_UNLOAD, { newBody: body });
  $('[data-turbolinks-permanent]').each((_index, element) => {
    if (element.id) {
      PERMANENT_ELEMENTS[element.id] = element;
    } else {
      console.warn('Permanent element must have an id:', element);
    }
  });
  document.title = title;

  // Move the permanent elements to the new document. This is done _before_ replacing the body to ensure that the permanent elements are available in the new document at the time the body is replaced. This prevents a few issues:
  // * Prevents weird flickering effects when the permanent elements are moved to the new document _after_ the body has been replaced
  // * Prevents web components from being disconnected, destroyed, and then reconnected and re-hydrated. By doing this, we ensure they are only disconnected and re-connected, preserving their state.
  for (let id in PERMANENT_ELEMENTS) {
    const element = PERMANENT_ELEMENTS[id];

    // Add a tombstone element to ensure we have an element with the same id in the DOM _after_ the permanent element has been moved to the new document. This is necessary to ensure that the element can be replaced with the old content when the back button is clicked.
    const tombstone = document.createElement('div');
    tombstone.id = id;
    element.insertAdjacentElement('beforebegin', tombstone);

    // Move the permanent element to the new document.
    element.dataset.turbolinksPermanentMoved = '';
    const replacedElement = body.querySelector(`:scope #${id}`);
    if (replacedElement) replacedElement.replaceWith(element);
  }

  // Replace the body of the current document with the body from the new document.
  document.documentElement.replaceChild(body, document.body);

  if (csrfToken != null) { CSRFToken.update(csrfToken); }
  if (canonicalUrl != null) { CanonicalURL.update(canonicalUrl); }
  setAutofocusElement();
  if (runScripts) { executeScriptTags(); }
  currentState = window.history.state;
  if (progressBar != null) {
    progressBar.done();
  }
  triggerEvent(EVENTS.CHANGE);
  return triggerEvent(EVENTS.UPDATE);
};

function executeScriptTags() {
  const scripts = Array.prototype.slice.call(document.body.querySelectorAll('script:not([data-turbolinks-eval="false"])'));
  for (let script of scripts) {
    if (['', 'text/javascript'].includes(script.type)) {
      Sentry.addBreadcrumb({
        message: 'Inserting script tag',
        category: 'xhr',
        data: {
          script: script.innerHTML
        }
      });
      const copy = document.createElement('script');
      for (let attr of script.attributes) { copy.setAttribute(attr.name, attr.value); }
      if (!script.hasAttribute('async')) { copy.async = false; }
      copy.appendChild(document.createTextNode(script.innerHTML));
      const { parentNode, nextSibling } = script;
      parentNode.removeChild(script);
      parentNode.insertBefore(copy, nextSibling);
    }
  }
};

function removeNoscriptTags(node) {
  node.innerHTML = node.innerHTML.replace(/<noscript[\S\s]*?<\/noscript>/ig, '');
  return node;
};

// Firefox bug: Doesn't autofocus fields that are inserted via JavaScript
function setAutofocusElement() {
  let list;
  const autofocusElement = (list = document.querySelectorAll('input[autofocus], textarea[autofocus]'))[list.length - 1];
  if (autofocusElement && (document.activeElement !== autofocusElement)) {
    return autofocusElement.focus();
  }
};

function reflectNewUrl(url) {
  url = new ComponentUrl(url);
  if (url.absolute !== referer) {
    try {
      return window.history.pushState({ turbolinks: true, url: url.absolute }, '', url.absolute);
    } catch (error) {}
  }
};
      // this might be called in an iframe that is hosted in another domain, we don't care about this error in this case

function reflectRedirectedUrl(xhr) {
  let location = xhr.getResponseHeader('X-XHR-Redirected-To');
  if (location) {
    location = new ComponentUrl(location);
    const preservedHash = location.hasNoHash() ? document.location.hash : '';
    return window.history.replaceState(window.history.state, '', location.href + preservedHash);
  }
};

function crossOriginRedirect(xhr) {
  const redirect = xhr.getResponseHeader('Location');
  if (redirect != null && (new ComponentUrl(redirect)).crossOrigin()) {
    return redirect;
  }
};

var rememberReferer = () => referer = document.location.href;

function rememberCurrentUrl() {
  try {
    return window.history.replaceState({ turbolinks: true, url: document.location.href }, '', document.location.href);
  } catch (error) {
    // Catch an unimportant error from IE11 when in an iframe.
  }
};

const rememberCurrentState = () => currentState = window.history.state;

// Unlike other browsers, Firefox doesn't trigger hashchange after changing the
// location (via pushState) to an anchor on a different page.  For example:
//
//   /pages/one  =>  /pages/two#with-hash
//
// By forcing Firefox to trigger hashchange, the rest of the code can rely on more
// consistent behavior across browsers.
function manuallyTriggerHashChangeForFirefox() {
  let url;
  if (navigator.userAgent.match(/Firefox/) && !(url = (new ComponentUrl)).hasNoHash()) {
    window.history.replaceState(currentState, '', url.withoutHash());
    return document.location.hash = url.hash;
  }
};

var recallScrollPosition = page => window.scrollTo(page.positionX, page.positionY);

function resetScrollPosition() {
  if (document.location.hash) {
    return document.location.href = document.location.href;
  } else {
    return window.scrollTo(0, 0);
  }
};

function clone(original) {
  if (original == null || typeof original !== 'object') {
    return original;
  }

  const copy = new original.constructor();
  for (let key in original) {
    const value = original[key];
    copy[key] = clone(value);
  }
  return copy;
};

function popCookie(name) {
  const match = document.cookie.match(new RegExp(name+"=(\\w+)"));
  const value = match ? match[1].toUpperCase() : '';
  document.cookie = name + '=; expires=Thu, 01-Jan-70 00:00:01 GMT; path=/';
  return value;
};

function triggerEvent(name, data) {
  if (typeof Prototype !== 'undefined') {
    Event.fire(document, name, data, true);
  }

  const event = document.createEvent('Events');
  if (data) { event.data = data; }
  event.initEvent(name, true, true);
  return document.dispatchEvent(event);
};

const pageChangePrevented = url => !triggerEvent(EVENTS.BEFORE_CHANGE, {url});

function processResponse(xhr) {
  const clientOrServerError = () => 400 <= xhr.status && xhr.status < 600;

  function validContent() {
    let contentType;
    return ((contentType = xhr.getResponseHeader('Content-Type')) != null) &&
      contentType.match(/^(?:text\/html|application\/xhtml\+xml|application\/xml)(?:;|$)/);
  };

  const extractTrackAssets = doc => (() => {
    const result = [];
    for (let node of doc.querySelector('head').childNodes) {
      if ((typeof node.getAttribute === 'function' ? node.getAttribute('data-turbolinks-track') : undefined) != null) {
        result.push(node.getAttribute('src') || node.getAttribute('href'));
      }
    }
    return result;
  })();

  function assetsChanged(doc) {
    if (!loadedAssets) { loadedAssets = extractTrackAssets(document); }
    const fetchedAssets  = extractTrackAssets(doc);
    return (fetchedAssets.length !== loadedAssets.length) || (intersection(fetchedAssets, loadedAssets).length !== loadedAssets.length);
  };

  function intersection(a, b) {
    if (a.length > b.length) { [a, b] = [b, a]; }
    return Array.from(a).filter((value) => Array.from(b).includes(value));
  };

  if (!clientOrServerError() && validContent()) {
    const doc = createDocument(xhr.responseText);
    if (doc && !assetsChanged(doc)) {
      return doc;
    }
  }
};

function extractTitleAndBody(doc) {
  const title = doc.querySelector('title');
  return [
    (title != null ? title.textContent : undefined),
    removeNoscriptTags(doc.querySelector('body')),
    CSRFToken.get(doc).token,
    CanonicalURL.get(doc).url,
    'runScripts'
  ];
};

var CSRFToken = {
  get(doc = document) {
    const tag = doc.querySelector('meta[name="csrf-token"]');
    return {
      node: tag,
      token: tag && tag.getAttribute ? tag.getAttribute('content') : undefined
    };
  },

  update(latest) {
    const current = this.get();
    if ((current.token != null) && (latest != null) && (current.token !== latest)) {
      return current.node.setAttribute('content', latest);
    }
  }
};

var CanonicalURL = {
  get(doc = document) {
    const tag = doc.querySelector('link[rel="canonical"]');
    return {
      node: tag,
      url: tag && tag.getAttribute ? tag.getAttribute('href') : undefined
    };
  },

  update(latest) {
    const current = this.get();
    if ((current.node != null) && (latest != null) && (current.url !== latest)) {
      return current.node.setAttribute('href', latest);
    }
  }
};

function createDocument(html) {
  const doc = document.documentElement.cloneNode();
  doc.innerHTML = html;
  doc.head = doc.querySelector('head');
  doc.body = doc.querySelector('body');
  return doc;
};

// The ComponentUrl class converts a basic URL string into an object
// that behaves similarly to document.location.
//
// If an instance is created from a relative URL, the current document
// is used to fill in the missing attributes (protocol, host, port).
class ComponentUrl {
  constructor(original = document.location.href) {
    this.original = original;
    if (this.original.constructor === ComponentUrl) { return this.original; }
    this._parse();
  }

  withoutHash() { return this.href.replace(this.hash, '').replace('#', ''); }

  // Intention revealing function alias
  withoutHashForIE10compatibility() { return this.withoutHash(); }

  hasNoHash() { return this.hash.length === 0; }

  crossOrigin() {
    return this.origin !== (new ComponentUrl).origin;
  }

  _parse() {
    (this.link != null ? this.link : (this.link = document.createElement('a'))).href = this.original;
    ({ href: this.href, protocol: this.protocol, host: this.host, hostname: this.hostname, port: this.port, pathname: this.pathname, search: this.search, hash: this.hash } = this.link);
    this.origin = [this.protocol, '//', this.hostname].join('');
    if (this.port.length !== 0) { this.origin += `:${this.port}`; }
    this.relative = [this.pathname, this.search, this.hash].join('');
    return this.absolute = this.href;
  }
}

// The Link class derives from the ComponentUrl class, but is built from an
// existing link element.  Provides verification functionality for Turbolinks
// to use in determining whether it should process the link when clicked.
const LINK_HTML_EXTENSIONS = ['html'];
class Link extends ComponentUrl {
  static allowExtensions(...extensions) {
    for (let extension of extensions) {
      LINK_HTML_EXTENSIONS.push(extension);
    }
    return LINK_HTML_EXTENSIONS;
  }

  constructor(link) {
    super();
    this.link = link;
    if (this.link.constructor === Link) { return this.link; }
    this.original = this.link.href;
    this.originalElement = this.link;
    this.link = this.link.cloneNode(false);

    // DS002: Fix invalid constructor
    // Copied the code from ComponentUrl's constructor to simulate the super at the end of the constructor
    this.original = link;
    if (this.original.constructor === ComponentUrl) { return this.original; }
    this._parse();
  }

  shouldIgnore() {
    return this.crossOrigin() ||
      this._anchored() ||
      this._billing() ||
      this._nonHtml() ||
      this._optOut() ||
      this._target();
  }

  _anchored() {
    return ((this.hash.length > 0) || (this.href.charAt(this.href.length - 1) === '#')) &&
      (this.withoutHash() === (new ComponentUrl).withoutHash());
  }

  _billing() {
    return this.pathname.includes('settings/account/billing');
  }

  _nonHtml() {
    return this.pathname.match(/\.[a-z]+$/g) && !this.pathname.match(new RegExp(`\\.(?:${LINK_HTML_EXTENSIONS.join('|')})?$`, 'g'));
  }

  _optOut() {
    let ignore;
    let link = this.originalElement;
    while (!ignore && (link !== document) && (link !== null)) {
      ignore = (link.getAttribute('data-no-turbolink') != null);
      link = link.parentNode;
    }
    return ignore;
  }

  _target() {
    return this.link.target.length !== 0;
  }
}

// The Click class handles clicked links, verifying if Turbolinks should
// take control by inspecting both the event and the link. If it should,
// the page change process is initiated. If not, control is passed back
// to the browser for default functionality.
class Click {
  static installHandlerLast(event) {
    if (!event.defaultPrevented) {
      document.removeEventListener('click', Click.handle, false);
      return document.addEventListener('click', Click.handle, false);
    }
  }

  static handle(event) {
    return new Click(event);
  }

  constructor(event) {
    this.event = event;
    if (this.event.defaultPrevented) { return; }
    this._extractLink();
    if (this._validForTurbolinks()) {
      if (!pageChangePrevented(this.link.absolute)) { visit(this.link.href); }
      this.event.preventDefault();
    }
  }

  _extractLink() {
    let link = this.event.target;
    while (!!link.parentNode && (link.nodeName !== 'A')) { link = link.parentNode; }
    if (link.nodeName === 'A' && link.href.length !== 0) {
      return this.link = new Link(link);
    }
  }

  _validForTurbolinks() {
    return (this.link != null) && !(this.link.shouldIgnore() || this._nonStandardClick());
  }

  _nonStandardClick() {
    return (this.event.which > 1) ||
      this.event.metaKey ||
      this.event.ctrlKey ||
      this.event.shiftKey ||
      this.event.altKey;
  }
}


const PROGRESS_BAR_CLASSNAME = 'turbolinks-progress-bar';
class ProgressBar {
  constructor(elementSelector) {
    this._trickle = this._trickle.bind(this);
    this.elementSelector = elementSelector;
    this.value = 0;
    this.content = '';
    this.speed = 300;
    // Setting the opacity to a value < 1 fixes a display issue in Safari 6 and
    // iOS 6 where the progress bar would fill the entire page.
    this.opacity = 0.99;
    this.install();
  }

  install() {
    this.element = document.querySelector(this.elementSelector);
    this.element.classList.add(PROGRESS_BAR_CLASSNAME);
    this.styleElement = document.createElement('style');
    document.head.appendChild(this.styleElement);
    return this._updateStyle();
  }

  uninstall() {
    this.element.classList.remove(PROGRESS_BAR_CLASSNAME);
    return document.head.removeChild(this.styleElement);
  }

  start() {
    return this.advanceTo(5);
  }

  advanceTo(value) {
    if (value > this.value && this.value <= 100) {
      this.value = value;
      this._updateStyle();

      if (this.value === 100) {
        return this._stopTrickle();
      } else if (this.value > 0) {
        return this._startTrickle();
      }
    }
  }

  done() {
    if (this.value > 0) {
      this.advanceTo(100);
      return this._reset();
    }
  }

  _reset() {
    const originalOpacity = this.opacity;

    setTimeout(() => {
      this.opacity = 0;
      return this._updateStyle();
    }
    , this.speed / 2);

    return setTimeout(() => {
      this.value = 0;
      this.opacity = originalOpacity;
      return this._withSpeed(0, () => this._updateStyle(true));
    }
    , this.speed);
  }

  _startTrickle() {
    if (this.trickling) { return; }
    this.trickling = true;
    return setTimeout(this._trickle, this.speed);
  }

  _stopTrickle() {
    return delete this.trickling;
  }

  _trickle() {
    if (!this.trickling) { return; }
    this.advanceTo(this.value + (Math.random() / 2));
    return setTimeout(this._trickle, this.speed);
  }

  _withSpeed(speed, fn) {
    const originalSpeed = this.speed;
    this.speed = speed;
    const result = fn();
    this.speed = originalSpeed;
    return result;
  }

  _updateStyle(forceRepaint = false) {
    if (forceRepaint) { this._changeContentToForceRepaint(); }
    return this.styleElement.textContent = this._createCSSRule();
  }

  _changeContentToForceRepaint() {
    return this.content = this.content === '' ? ' ' : '';
  }

  _createCSSRule() {
    return `\
${this.elementSelector}.${PROGRESS_BAR_CLASSNAME}::before {
content: '${this.content}';
position: fixed;
top: 0;
left: 0;
z-index: 2000;
background-color: #0076ff;
height: 3px;
opacity: ${this.opacity};
width: ${this.value}%;
transition: width ${this.speed}ms ease-out, opacity ${this.speed / 2}ms ease-in;
transform: translate3d(0,0,0);
}\
`;
  }
};

// Delay execution of function long enough to miss the popstate event
// some browsers fire on the initial page load.
const bypassOnLoadPopstate = fn => setTimeout(fn, 500);

const installDocumentReadyPageEventTriggers = () => document.addEventListener('DOMContentLoaded', ( function() {
  triggerEvent(EVENTS.CHANGE);
  return triggerEvent(EVENTS.UPDATE);
}), true);

function installJqueryAjaxSuccessPageUpdateTrigger() {
  if (typeof jQuery !== 'undefined') {
    return jQuery(document).on('ajaxSuccess', function(event, xhr, settings) {
      if (!jQuery.trim(xhr.responseText)) { return; }
      return triggerEvent(EVENTS.UPDATE);
    });
  }
};

function installHistoryChangeHandler(event) {
  if (event.state && event.state.turbolinks) {
    // Don't transition on reports pages (react-router will handle).
    if (event.state.url && event.state.url.includes(`${window.location.origin}/reports`)) {
      return;
    }

    const cachedPage = pageCache[(new ComponentUrl(event.state.url)).absolute];
    if (cachedPage && !cachedPage.transitionCacheDisabled) {
      cacheCurrentPage();
      return fetchHistory(cachedPage);
    } else {
      return visit(event.target.location.href);
    }
  }
};

function initializeTurbolinks() {
  rememberCurrentUrl();
  rememberCurrentState();

  document.addEventListener('click', Click.installHandlerLast, true);

  window.addEventListener('hashchange', function(event) {
    rememberCurrentUrl();
    return rememberCurrentState();
  }
  , false);
  return bypassOnLoadPopstate(() => window.addEventListener('popstate', installHistoryChangeHandler, false));
};

// Handle bug in Firefox 26/27 where history.state is initially undefined
const historyStateIsDefined =
  (window.history.state !== undefined) || navigator.userAgent.match(/Firefox\/2[6|7]/);

const browserSupportsPushState =
  window.history && window.history.pushState && window.history.replaceState && historyStateIsDefined;

const browserIsntBuggy =
  !navigator.userAgent.match(/CriOS\//);

let needle;
const requestMethodIsSafe =
  (needle = popCookie('request_method'), ['GET',''].includes(needle));

var browserSupportsTurbolinks = browserSupportsPushState && browserIsntBuggy && requestMethodIsSafe;

const browserSupportsCustomEvents =
  document.addEventListener && document.createEvent;

if (browserSupportsCustomEvents) {
  installDocumentReadyPageEventTriggers();
  installJqueryAjaxSuccessPageUpdateTrigger();
}

if (browserSupportsTurbolinks) {
  visit = fetch;
  initializeTurbolinks();
} else {
  visit = url => document.location.href = url;
}

// Perform a turbolinks visit when returning to a react-router URL.
window.addEventListener('popstate', function(e) {
  const currentUrl = currentState != null ? currentState.url : undefined;
  const reportsUrl = `${window.location.origin}/reports`;

  // Don't do a Turbolinks visit if react-router is handling the transition.
  if ((currentUrl === undefined) || currentUrl.startsWith(reportsUrl)) { return; }

  // Don't do a Turbolinks visit if not moving from non-react-router to react-router.
  if (!e.target.location.href.startsWith(reportsUrl)) { return; }

  // Manually perform visit to return to dashboard.
  visit(e.pathname);
});

// Public API
//   Turbolinks.visit(url)
//   Turbolinks.pagesCached()
//   Turbolinks.pagesCached(20)
//   Turbolinks.enableTransitionCache()
//   Turbolinks.allowLinkExtensions('md')
//   Turbolinks.supported
//   Turbolinks.EVENTS
const Turbolinks = {
  visit,
  pagesCached,
  enableTransitionCache,
  enableProgressBar,
  allowLinkExtensions: Link.allowExtensions,
  supported: browserSupportsTurbolinks,
  EVENTS: clone(EVENTS)
};

export default Turbolinks;
